import React, { useEffect, useMemo, useRef, useState } from "react";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { IColumn, IFilter, IPagedListAction, IPagedListRowIndicator, IPagedViewResponse, RowFormatterFunction, SortOrder } from "../Declarations";
import { SelectColumnModal } from "../SelectColumnModal";
import { usePagedListData, usePagedListColumns, usePagedListFilters, usePagedListExport } from "../../../hooks";
import { PagedListHeader } from "./PagedListHeader";
import { PagedListTable } from "./PagedListTable";
import { PagedListPagination } from "./PagedListPagination";
import { separateActions, getRowClassName, buildPagesArray, FilterMigration, resolvePagedListRowIndicators, ResolveStaticFilterValues } from "../../../utils";
import "../PagedList.css";

const EMPTY_STATIC_FILTER_VALUES: Record<string, unknown> = {};

type Props = {
    columns: IColumn[];
    headActions?: IPagedListAction[];
    itemActions?: IPagedListAction[];
    filters?: IFilter[];
    staticFilterValues?: Record<string, any>;
    resolveStaticFilterValues?: ResolveStaticFilterValues;
    defaultSortBy: string;
    defaultSortOrder?: SortOrder;
    pageSize?: number;
    getUrl: string;
    avatarUrl?: string;
    keyColumn?: string;
    rowFormatter: RowFormatterFunction;
    defaultRowClassName?: string;
    persistanceKey?: string;
    filterMigrations?: Record<string, FilterMigration>;
    refreshNumber?: number;
    rowActionsAriaLabel?: string;
    exportUrl?: string;
    exportIcon?: IconDefinition;
    items?: IPagedViewResponse;
    csvExportUrl?: string;
    showDeletedRowIndicator?: boolean;
    rowIndicators?: IPagedListRowIndicator[];
};

const PagedList: React.FC<Props> = ({
    columns,
    headActions = [] as IPagedListAction[],
    itemActions = [] as IPagedListAction[],
    filters = [] as IFilter[],
    staticFilterValues = EMPTY_STATIC_FILTER_VALUES,
    resolveStaticFilterValues,
    defaultSortBy,
    defaultSortOrder = SortOrder.ascending,
    pageSize = 25,
    getUrl,
    avatarUrl,
    keyColumn = "id",
    rowFormatter,
    defaultRowClassName = "bg-body",
    persistanceKey,
    filterMigrations,
    refreshNumber,
    rowActionsAriaLabel,
    exportUrl,
    exportIcon,
    items,
    csvExportUrl,
    showDeletedRowIndicator,
    rowIndicators,
}) => {
    const effectiveRowIndicators = useMemo(
        () => resolvePagedListRowIndicators(showDeletedRowIndicator, rowIndicators),
        [showDeletedRowIndicator, rowIndicators]
    );

    // Custom hooks for different concerns
    const dataHook = usePagedListData({
        getUrl,
        pageSize,
        defaultSortBy,
        defaultSortOrder,
        refreshNumber,
        items
    });

    const columnsHook = usePagedListColumns({
        columns,
        persistanceKey
    });

    const filtersHook = usePagedListFilters({
        filters,
        staticFilterValues,
        resolveStaticFilterValues,
        persistanceKey,
        filterMigrations,
    });

    const exportHook = usePagedListExport({
        exportUrl,
        csvExportUrl,
        pageSize,
        staticFilterValues,
        resolveStaticFilterValues,
        filterValues: filtersHook.filterValues,
        sortBy: dataHook.sortBy,
        sortOrder: dataHook.sortOrder
    });

    // Local state
    const [pages, setPages] = useState<number[]>([]);

    // Separate actions
    const { primaryActions, secondaryActions } = separateActions(itemActions);

    // Build pages when data changes
    useEffect(() => {
        if (dataHook.total && dataHook.total > 0) {
            const pagesArray = buildPagesArray(dataHook.offset, dataHook.total, pageSize);
            setPages(pagesArray);
        }
    }, [dataHook.total, dataHook.offset, pageSize]);

    // Fetch data when parameters change
    const lastRequestValuesRef = useRef(filtersHook.lastRequestValues);
    lastRequestValuesRef.current = filtersHook.lastRequestValues;

    useEffect(() => {
        if (!filtersHook.filtersReady) {
            return;
        }

        const requestValues = filtersHook.buildRequestValues(
            dataHook.offset,
            pageSize,
            dataHook.sortBy,
            dataHook.sortOrder
        );

        const last = lastRequestValuesRef.current;
        if (!last || filtersHook.requestParamsChanged(last, requestValues)) {
            dataHook.getResults(requestValues);
            filtersHook.setLastRequestValues(requestValues);
            lastRequestValuesRef.current = requestValues;
        }
    }, [
        dataHook.offset,
        pageSize,
        dataHook.sortBy,
        dataHook.sortOrder,
        filtersHook.filterValues,
        filtersHook.filtersReady,
        filtersHook.buildRequestValues,
        filtersHook.requestParamsChanged,
        dataHook.getResults,
        filtersHook.setLastRequestValues,
    ]);

    // Refetch when parent bumps refreshNumber (e.g. after row mutation) without changing filters/sort/page.
    useEffect(() => {
        if (refreshNumber === undefined || refreshNumber === 0) return;

        const requestValues = filtersHook.buildRequestValues(
            dataHook.offset,
            pageSize,
            dataHook.sortBy,
            dataHook.sortOrder
        );
        dataHook.getResults(requestValues);
        filtersHook.setLastRequestValues(requestValues);
    }, [refreshNumber]);

    const handleScroll = (value: number) => {
    };

    const getRowClassNameForItem = (item: any) => {
        return getRowClassName(item, defaultRowClassName, rowFormatter);
    };

    return (
        <div>
            <PagedListHeader
                filterValues={filtersHook.filterValues}
                onFilterValueChanged={filtersHook.handleFilterValueChanged}
                onFilterReset={filtersHook.handleFilterReset}
                total={dataHook.total || 0}
                first={dataHook.first || 0}
                last={dataHook.last || 0}
                headActions={headActions}
                exportUrl={exportUrl}
                csvExportUrl={csvExportUrl}
                exportIcon={exportIcon}
                onDownloadExport={exportHook.downloadExport}
                onExportCsv={exportHook.handleExportCsv}
            />

            <PagedListTable
                visibleColumns={columnsHook.visibleColumns}
                results={dataHook.results || []}
                hasLoadError={dataHook.hasLoadError}
                loadErrorMessage={dataHook.loadErrorMessage || null}
                hasResults={dataHook.hasResults}
                total={dataHook.total || 0}
                isBusy={dataHook.isBusy}
                tableWidth={columnsHook.tableWidth}
                sortBy={dataHook.sortBy}
                sortOrder={dataHook.sortOrder}
                primaryActions={primaryActions}
                secondaryActions={secondaryActions}
                keyColumn={keyColumn}
                avatarUrl={avatarUrl}
                rowActionsAriaLabel={rowActionsAriaLabel}
                onSortByClicked={dataHook.handleSortByClicked}
                onSelectColumnsClicked={columnsHook.handleSelectColumnsClicked}
                onScroll={handleScroll}
                getRowClassName={getRowClassNameForItem}
                rowIndicators={effectiveRowIndicators}
            />

            <PagedListPagination
                pages={pages}
                currentPage={dataHook.currentPage}
                lastPage={dataHook.lastPage}
                onPageChange={dataHook.setCurrentPage}
            />

            <SelectColumnModal
                columnOptions={columnsHook.columnOptions}
                show={columnsHook.showSelectColumnsModal}
                onSave={columnsHook.handleSelectColumnsUpdated}
                onReset={columnsHook.handleSelectColumnsReset}
                onClose={() => columnsHook.setShowSelectColumnsModal(false)}
            />
        </div>
    );
};

export { PagedList };
